import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useAuth } from '@/auth/AuthProvider'
import { FxAlert, FxAvatar, FxButton, FxIcon, FxSpinner, FxTextField } from '@/components/fx'
import { accessApi } from '@/lib/api'
import type { AccessRole } from '@/types/domain'

import { ShareDialogShell } from './ShareDialogShell'
import { UploadLinkSection } from './UploadLinkSection'
import {
  ROLE_EXPLANATIONS,
  ROLE_LABELS,
  ROLE_ORDER,
  inviteErrorMessage,
  isEditable,
  isUserNotFound,
  roleChangeRefusal,
  shareRows,
  type RoleChangeRefusal,
  type ShareRow,
} from './shareModel'

/**
 * Freigeben: who may open this form, and the phone links that upload into it.
 *
 * Two things that are not sharing in the same sense sit in one dialog on
 * purpose — both answer "who gets at this form", and the alternative was a
 * second dialog for a card with one button in it. The artboard shows the
 * upload link as a cockpit side card; that card stays, this is where the
 * links are managed.
 *
 * What the gateway allows shapes every control here:
 *  - one person's role is one `PATCH …/access/:userId` (`accessApi.setRole`)
 *    and touches nothing else. It used to be a read-modify-write over
 *    `PUT …/access`, a full replace, which deleted any invitation that
 *    landed between the read and the save;
 *  - it grants nothing: a person with no direct entry is refused, and the
 *    dialog offers the invitation that would actually give them one;
 *  - there are no e-mail invitations, so an address without an account is a
 *    404 and is said so plainly;
 *  - a folder-inherited membership is not an entry on this template and
 *    cannot be touched here at all.
 */
export interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string
  templateTitle?: string
  /** The project crumb for the subtitle; omitted when the form has none. */
  folderName?: string
  /** `canManage` from `GET …/access`: may this person change the sharing. */
  canManage: boolean
  /** Write access: what listing and minting upload links needs (`writeMW`). */
  canWrite: boolean
}

export function ShareDialog({
  open,
  onOpenChange,
  templateId,
  templateTitle,
  folderName,
  canManage,
  canWrite,
}: ShareDialogProps) {
  const subtitle = [templateTitle, folderName ? `im Projekt „${folderName}“` : null]
    .filter(Boolean)
    .join(' · ')

  return (
    <ShareDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title="Freigeben"
      description={subtitle || undefined}
      footer={
        <>
          <span className="flex-1 text-small text-text-muted">
            Freigaben des Projekts gelten automatisch auch hier.
          </span>
          <FxButton onClick={() => onOpenChange(false)}>Fertig</FxButton>
        </>
      }
    >
      {/* The body mounts only while the dialog is open: it runs two queries,
          and neither should be in flight behind a closed dialog. */}
      {open && (
        <ShareDialogBody templateId={templateId} canManage={canManage} canWrite={canWrite} />
      )}
    </ShareDialogShell>
  )
}

function ShareDialogBody({
  templateId,
  canManage,
  canWrite,
}: {
  templateId: string
  canManage: boolean
  canWrite: boolean
}) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const access = useQuery({
    queryKey: ['access', templateId],
    queryFn: () => accessApi.get(templateId),
    enabled: templateId !== '',
  })

  const entries = access.data?.entries ?? []

  // A refused role change, and whether an invitation is the way out of it.
  // There is no "somebody else changed this too" notice any more: the race
  // that needed one was the full replace, and a PATCH of one row cannot
  // overwrite anybody else's change.
  const [refusal, setRefusal] = useState<
    (RoleChangeRefusal & { row: ShareRow; role: AccessRole }) | null
  >(null)
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['access', templateId] })

  const [email, setEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<AccessRole>('write')
  const invite = useMutation({
    mutationFn: (input: { email: string; role: AccessRole }) =>
      accessApi.invite(templateId, { email: input.email.trim(), role: input.role }),
    onSuccess: () => {
      setEmail('')
      setRefusal(null)
      void refresh()
    },
  })

  /**
   * One person's role: one request, and it touches nobody else.
   *
   * This used to read the whole access list, change the one row and write it
   * all back through `PUT …/access` — a full replace. Two managers working at
   * the same time overwrote each other, and an invitation that landed between
   * the read and the save was deleted by it. The re-read-and-compare that
   * stood here could only *notice* that, never prevent it. `PATCH
   * …/access/:userId` makes it a non-question, so both are gone.
   */
  const changeRole = useMutation({
    mutationFn: ({ row, role }: { row: ShareRow; role: AccessRole }) =>
      accessApi.setRole(templateId, row.userId, role),
    onSuccess: () => {
      setRefusal(null)
      void refresh()
    },
    onError: (error, variables) => {
      // The role carried along is the one that was just picked, so an
      // invitation offered from here gives the person what the click meant,
      // not the role the stale row happens to show.
      setRefusal({ ...roleChangeRefusal(error), row: variables.row, role: variables.role })
      // The entry is not what the dialog thought it was, so the list on
      // screen is stale — but only for a refusal that says something about
      // the entry. A failed lookup (500) says nothing, and re-reading is
      // harmless either way.
      void refresh()
    },
  })

  const remove = useMutation({
    mutationFn: (userId: string) => accessApi.remove(templateId, userId),
    onSuccess: () => {
      setRefusal(null)
      void refresh()
    },
  })

  const rows = shareRows(entries, user?.id)
  const hasFolderRows = rows.some((row) => row.kind === 'folder')

  return (
    <>
      {canManage && (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (!email.trim() || invite.isPending) return
            invite.mutate({ email, role: inviteRole })
          }}
        >
          <FxTextField
            containerClassName="min-w-[220px] flex-1"
            label="E-Mail-Adresse"
            type="email"
            autoComplete="off"
            placeholder="E-Mail-Adresse eingeben"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <RoleSelect
            label="Rolle"
            value={inviteRole}
            onChange={setInviteRole}
            disabled={invite.isPending}
          />
          <FxButton type="submit" disabled={!email.trim() || invite.isPending}>
            Einladen
          </FxButton>
        </form>
      )}

      {invite.isError && (
        // An address without an account is the ordinary outcome of a system
        // with no e-mail invitations, not a breakdown, so it is told in the
        // calmer tone.
        <FxAlert tone={isUserNotFound(invite.error) ? 'info' : 'danger'}>
          {inviteErrorMessage(invite.error)}
        </FxAlert>
      )}

      {/* A refused role change. `access_entry_not_found` is not a failure —
          the person simply has no entry of their own here (they may reach the
          form through the project), and an invitation is what gives them one,
          so it is offered in place of an error. Anything else, a failed
          lookup included, says only that the role did not change: the
          gateway distinguishes the two now, and claiming somebody's access
          was removed because a request fell over would be a lie. */}
      {refusal && (
        <FxAlert tone={refusal.offerInvite ? 'info' : 'danger'}>
          <span className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{refusal.message}</span>
            {refusal.offerInvite && refusal.row.email && (
              <FxButton
                variant="outlined"
                disabled={invite.isPending}
                onClick={() =>
                  invite.mutate({ email: refusal.row.email as string, role: refusal.role })
                }
              >
                {`${refusal.row.name} einladen`}
              </FxButton>
            )}
          </span>
        </FxAlert>
      )}

      {remove.isError && <FxAlert tone="danger">Der Zugriff wurde nicht entfernt.</FxAlert>}

      {access.isPending && <FxSpinner label="Freigaben werden geladen …" />}
      {access.isError && (
        <FxAlert tone="danger" title="Die Freigaben konnten nicht geladen werden">
          <FxButton variant="outlined" className="mt-4" onClick={() => void access.refetch()}>
            Erneut versuchen
          </FxButton>
        </FxAlert>
      )}

      {access.isSuccess && (
        <ul className="m-0 flex list-none flex-col p-0">
          {rows.map((row) => (
            <MemberRow
              key={row.userId}
              row={row}
              canManage={canManage}
              busy={changeRole.isPending || remove.isPending}
              onRole={(role) => changeRole.mutate({ row, role })}
              onRemove={() => remove.mutate(row.userId)}
            />
          ))}
        </ul>
      )}

      {hasFolderRows && (
        <p className="m-0 text-small text-text-muted">
          „über Projekt“ heißt: Die Person gehört zum Projekt und kann hier nicht entfernt
          werden. Ändern lässt sich das unter „Projekte“ in der Freigabe des Projekts.
        </p>
      )}

      {!canManage && access.isSuccess && (
        <p className="m-0 text-small text-text-muted">
          Freigaben ändern dürfen nur Personen mit der Rolle „Verwalten“.
        </p>
      )}

      <dl className="m-0 grid grid-cols-[minmax(84px,110px)_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-sm bg-ground p-4 text-small">
        {ROLE_EXPLANATIONS.map((item) => (
          <div key={item.role} className="contents">
            <dt className="font-semibold">{ROLE_LABELS[item.role]}</dt>
            <dd className="m-0">{item.text}</dd>
          </div>
        ))}
      </dl>

      <UploadLinkSection templateId={templateId} canWrite={canWrite} />
    </>
  )
}

function MemberRow({
  row,
  canManage,
  busy,
  onRole,
  onRemove,
}: {
  row: ShareRow
  canManage: boolean
  busy: boolean
  onRole: (role: AccessRole) => void
  onRemove: () => void
}) {
  // Removing someone is instant and silent for them, so it asks first. A
  // second dialog over this one would trap focus twice; the row asks in place.
  const [confirming, setConfirming] = useState(false)
  const editable = canManage && isEditable(row)

  return (
    <li className="flex min-h-[52px] flex-wrap items-center gap-3 border-b border-divider py-2 last:border-b-0">
      <FxAvatar initials={row.initials} size={36} filled={row.isSelf} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-semibold">
          {row.name}
          {row.isSelf && ' (Sie)'}
        </span>
        {row.email && <span className="truncate text-small text-text-muted">{row.email}</span>}
      </span>

      {row.kind === 'owner' && (
        <span className="rounded-sm bg-fill px-3 py-0.5 text-small">Eigentümer:in</span>
      )}

      {row.kind === 'folder' && (
        <span className="text-small text-text-muted">{ROLE_LABELS[row.role]} · über Projekt</span>
      )}

      {row.kind === 'direct' &&
        (editable ? (
          confirming ? (
            <span className="flex items-center gap-2 text-small">
              <span>Zugriff entfernen?</span>
              <FxButton
                variant="dangerTonal"
                className="h-9 px-3 text-small"
                disabled={busy}
                onClick={() => {
                  setConfirming(false)
                  onRemove()
                }}
              >
                Entfernen
              </FxButton>
              <FxButton
                variant="outlined"
                className="h-9 px-3 text-small"
                onClick={() => setConfirming(false)}
              >
                Abbrechen
              </FxButton>
            </span>
          ) : (
            <>
              <RoleSelect
                srLabel={`Rolle von ${row.name}`}
                value={row.role}
                onChange={onRole}
                disabled={busy}
              />
              <button
                type="button"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-sm text-text-muted"
                aria-label={`Zugriff von ${row.name} entfernen`}
                onClick={() => setConfirming(true)}
              >
                <FxIcon name="close" size={20} />
              </button>
            </>
          )
        ) : (
          <span className="text-small text-text-muted">{ROLE_LABELS[row.role]}</span>
        ))}
    </li>
  )
}

function RoleSelect({
  label,
  srLabel,
  value,
  onChange,
  disabled,
}: {
  label?: string
  srLabel?: string
  value: AccessRole
  onChange: (role: AccessRole) => void
  disabled?: boolean
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className={label ? 'text-body' : 'sr-only'}>{label ?? srLabel}</span>
      <select
        className="h-control w-[150px] rounded-md bg-surface px-3 text-body text-text shadow-raised"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value as AccessRole)}
      >
        {ROLE_ORDER.map((role) => (
          <option key={role} value={role}>
            {ROLE_LABELS[role]}
          </option>
        ))}
      </select>
    </label>
  )
}
